import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { UnsubscribeService } from '@webgate/angular-azure-core';
import { Actions } from 'src/app/models/Actions';
import { Keyword, KeywordValue } from 'src/app/models/keyword';
import { CompareService } from 'src/app/services/compare.service';
import { KeywordsService } from 'src/app/services/keywords.service';
import { KeywordAddFormFactory } from 'src/app/utils/factories/keywordAddFormFactory';
import { KeywordFormFactory } from 'src/app/utils/factories/keywordFormFactory';

import { AddKeywordValueComponent } from './add-keyword-value/add-keyword-value.component';
@Component({
  templateUrl: './edit-keyword-dialog.component.html',
  styleUrls: ['./edit-keyword-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    DragDropModule
  ]
})
export class AdminKeywordEditDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AdminKeywordEditDialogComponent>>(MatDialogRef);
  data = inject<{
    keyword: Keyword;
  }>(MAT_DIALOG_DATA);

  keywordsService = inject(KeywordsService);
  unsubscribeService = inject(UnsubscribeService);
  formBuilder = inject(NonNullableFormBuilder);
  dialog = inject(MatDialog);
  compareService = inject(CompareService);
  keywordFormFactory = inject(KeywordFormFactory);
  keywordAddFormFactory = inject(KeywordAddFormFactory);
  keyword!: Keyword;
  keywordFormGroup!: FormGroup;
  translations: Record<string, string> = {};
  isNewDoc = false;

  ngOnInit(): void {
    //we need a deep clone of the passed in keyword to avoid updating before saving
    const kw = structuredClone(this.data.keyword || {});
    this.loadKeyword(kw as Keyword);
  }

  loadKeyword(keyword: Keyword) {
    this.keyword = keyword;
    if (!this.keyword || this.keyword.id === '') {
      this.isNewDoc = true;
    }
    this.keywordFormGroup = this.keywordFormFactory.build(this.keyword);
  }

  addEntry(): void {
    const ref = this.dialog.open(AddKeywordValueComponent, { data: { value: undefined, isNew: true }, width: '600px' });
    ref
      .afterClosed()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (keywordValue) => {
          if (keywordValue) {
            this.FormArray.push(this.keywordAddFormFactory.build(keywordValue));
          }
        }
      });
  }

  removeEntry(index: number): void {
    this.FormArray.removeAt(index);
  }

  editEntry(entryRef: KeywordValue, index: number): void {
    const ref = this.dialog.open(AddKeywordValueComponent, { data: { value: entryRef, isNew: false }, width: '600px' });
    ref
      .afterClosed()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (keywordValue) => {
          if (keywordValue) {
            this.FormArray.at(index).patchValue(keywordValue);
          }
        }
      });
  }

  btnSortValues(): void {
    const listOfValue = this.FormArray?.value as KeywordValue[];
    listOfValue.sort((a, b) => this.compareService.compare(a.keywordValueName, b.keywordValueName, true));
    this.FormArray.patchValue(listOfValue);
  }

  dropSortObj(event: CdkDragDrop<string[]>): void {
    const currentMove = this.FormArray.at(event.previousIndex);
    this.FormArray.removeAt(event.previousIndex);
    this.FormArray.insert(event.currentIndex, currentMove);
  }

  trySaveKeyword(): void {
    if (this.keywordFormGroup.invalid) return;
    this.saveKeyword(this.keywordFormGroup.value as Keyword);
  }

  btnDelete(): void {
    if (!confirm('Do you really want to delete this Keyword?')) return;
    this.keywordsService
      .deleteKeyword(this.keyword.id)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: () => this.dialogRef.close(Actions.Refresh)
      });
  }

  saveKeyword(keyword: Keyword): void {
    this.keywordsService
      .saveKeyword(keyword)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: () => this.dialogRef.close(Actions.Refresh)
      });
  }

  get FormArray() {
    return this.keywordFormGroup.get('keywordValues') as FormArray;
  }
}
